import { Close, MenuOutlined } from '@mui/icons-material'
import { Drawer, IconButton, useMediaQuery, styled } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { getIsLightBackground } from '../Misc/utils'
import { colors } from '../../../tokens/colors'
import { typography } from '../../../tokens/typography'

type Props = {
  colorTheme?: string
  headerOffset?: number
  scrollOffset: number
  navOffset: number
  clickCallback?: (isOpen: boolean) => void
}

const getHeaders = (headerOffset: number) => {
  // try to get the container, if it doesn't exist, use the document
  const headerNodeList = document.querySelectorAll('h1, h2')
  if (!headerNodeList) {
    return []
  }

  const headers = Array.from(headerNodeList).map((header: Element) => {

    // Keep the previous attributes such as text alignment
    const previousAttribites = header.getAttribute('style')
    // add scroll margin to account for the fixed header
    header.setAttribute('style', `scroll-margin-top: ${headerOffset + 8}px; ${previousAttribites}`)
    return header
  })
  return headers
}

export const StoryNavigation = ({
  colorTheme,
  headerOffset,
  scrollOffset,
  navOffset,
  clickCallback = () => {},
}: Props): JSX.Element => {
  const isSmallOrBelow = useMediaQuery('(max-width:959.95px)')
  const isLargeOrAbove = useMediaQuery('(min-width:1106px)')
  const [hasClicked, setHasClicked] = useState<boolean>(false)
  const [drawerOpen, setDrawerOpen] = useState<undefined | boolean>(undefined)
  const [headers, setHeaders] = useState<Element[]>([])

  // USEMEDIAQUERY ISN'T AVAILABLE IMMEDIATELY ON RENDER SO
  // THIS SETS THE DEFAULT STATE OF THE DRAWER WHEN READY
  // UNLESS THE USER HAS SET THE TOC STATE IT WILL BE RESPECTED
  useEffect(() => {
    if (!hasClicked) setDrawerOpen(isLargeOrAbove)
  }, [hasClicked, isLargeOrAbove])

  // THIS FIRES ON COMPONENT RENDER TO GET ALL H1/H2 ON THE PAGE AND ADD SCROLL MARGIN
  useEffect(() => {
    setHeaders(getHeaders(isSmallOrBelow ? 0 : scrollOffset))
  }, [])

  const scrollToItem = (header: Element) => {
    if (header) {
      header.scrollIntoView({ behavior: 'smooth' })
    }

    if (isSmallOrBelow) {
      handleClick(false)
    }
  }

  const isLightBackground = getIsLightBackground(colorTheme)
  const color = isLightBackground
    ? colors.copy.headerDark
    : colors.copy.headerLight

  const handleClick = (newVal: boolean) => {
    setHasClicked(true)
    clickCallback(newVal)
    setDrawerOpen(newVal)
  }

  if (headers.length) {
    return (
      <>
        <NavIcon
          navOffset={navOffset}
          onClick={() => handleClick(true)}
          backgroundColor={colorTheme}
          isLightBackground={isLightBackground}
          iconColor={color}
          sx={{
            top: `calc(22px + ${navOffset}px)`,
          }}
        >
          <MenuOutlined />
        </NavIcon>
        <Drawer
          anchor={isSmallOrBelow ? 'top' : 'left'}
          open={drawerOpen === true}
          variant='persistent'
          sx={{
            width: isSmallOrBelow ? '100%' : '288px',
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              height: `calc(100vh - ${headerOffset}px)`,
              background: colorTheme,
              boxSizing: 'border-box',
              top: `${isSmallOrBelow ? 0 : headerOffset}px`,
            },
          }}
        >
          <DrawerContent color={color} isSmallOrBelow={isSmallOrBelow}>
            <NavHeader>
              <div>Table of Contents</div>
              <CloseButton
                size='small'
                onClick={() =>
                  handleClick(isSmallOrBelow ? !drawerOpen : false)
                }
                isLightBackground={isLightBackground}
              >
                <Close />
              </CloseButton>
            </NavHeader>
            {headers.map((header, index) => (
              <NavItem
                key={index}
                tabIndex={0}
                isLightBackground={isLightBackground}
                role='link'
                headerType={header.tagName}
                onClick={() => scrollToItem(header)}
                onKeyUp={(e) => {
                  if (e.code === 'Enter') {
                    scrollToItem(header)
                  }
                }}
              >
                {header.textContent}
              </NavItem>
            ))}
          </DrawerContent>
        </Drawer>
      </>
    )
  }
  return <></>
}

const DrawerContent = styled('div')<{
  color: string
  isSmallOrBelow?: boolean
}>`
  width: ${(props) => (props.isSmallOrBelow ? '100%' : '288px')};
  color: ${(props) => props.color};
  padding-bottom: 12px;
  svg {
    color: ${(props) => props.color};
  }
`

const NavHeader = styled('div')`
  padding-bottom: 24px;
  align-items: center;
  display: flex;
  padding: 30px 24px 24px 24px;
  justify-content: space-between;
  ${typography.up1.semiBoldSans}
`

const NavItem = styled('div')<{
  headerType: string
  isLightBackground?: boolean
}>`
  text-decoration: ${(props) => (props.headerType === 'H1' ? 'underline' : '')};
  padding: 12px 24px 12px 24px;
  padding-left: ${(props) => (props.headerType === 'H1' ? '24px' : '36px')};
  ${typography.down1.semiBoldSans}
  cursor: pointer;

  &:focus-visible,
  &:hover {
    outline: none;
    background: ${(props) =>
      props.isLightBackground ? 'rgba(0,0,0,.05)' : 'rgba(255,255,255,.05)'};
  }
`

const NavIcon = styled(IconButton)<{
  backgroundColor?: string
  isLightBackground: boolean
  navOffset: number
  iconColor: string
}>`
  position: fixed;
  z-index: 3;
  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.iconColor};
  left: 22px;
  top: ${(props) => `calc(22px + ${props.navOffset || 0}px)`};
  box-shadow: 2px 4px 8px 0px rgba(0, 50, 99, 0.07);
  &:focus-visible,
  &:hover {
    background: ${({ backgroundColor }) => `${backgroundColor}b4`};
  }

  @media screen and (max-width: 959px) {
    top: initial;
    left: initial;
    bottom: 22px;
    right: 22px;
  }
`

const CloseButton = styled(IconButton)<{
  isLightBackground: boolean
}>`
  &:focus-visible,
  &:hover {
    background: ${(props) =>
      props.isLightBackground ? 'rgba(0,0,0,.1)' : 'rgba(255,255,255,.1)'};
  }
`
